import React, { useEffect, useState } from 'react';
import styled from 'styled-components/macro';
import { compose } from 'ramda';
import { RouteComponentProps } from 'react-router-dom';
import { OrganizationType } from 'store/modules/auth/auth';
import { BackTo, Typography } from '@deeptrue-frontend/ui';
import { Dashboard } from 'components/Layout';
import api from 'api';

import StoreProvider from 'components/StoreProvider/StoreProvider';
import { ProjectType } from 'store/modules/projects/projects.types';
import { withMetaDescription } from 'components/HeadProvider/HeadProvider';
import { withProject } from '../withProject';
import { useFormatMessage } from 'components/IntlProvider';
import Forms from 'components/Forms';
import { FormValues } from 'components/Forms/TemplateCreator/TemplateCreator';
import ROUTES from 'consts/routing';
import { TemplateGroup } from 'consts/template';
import { generateContent } from './generatePreview';

const { connect, selectors } = StoreProvider;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const FormWrapper = styled.div`
  margin-top: 1.5rem;
  background-color: white;
  border: 1px solid #e3e5e8;
  border-radius: 4px;
  box-shadow: 0px 2px 4px rgba(63, 63, 68, 0.041958),
    0px 1px 1px rgba(63, 63, 68, 0.0769231);
`;

interface Props
  extends RouteComponentProps<{
    id: string;
    codename: string;
    templateId: string;
  }> {
  organization: OrganizationType;
  project: ProjectType;
}

const TemplateCreator = ({
  organization,
  project,
  location,
  match: { path, params },
  history,
}: Props) => {
  const [templateData, setTemplateData] = useState<{
    templateName: string;
    subject: string;
    content: string;
    text_content: string;
  }>({
    templateName: '',
    subject: '',
    content: '',
    text_content: '',
  });
  const { templateId } = params;

  useEffect(() => {
    if (templateId) {
      api.resources.templates
        .getEmailTemplate({
          orgName: params.codename || organization.codename,
          templateId: Number(templateId),
        })
        .then(({ data }) => {
          const { name, content, subject, text_content } = data;

          setTemplateData({
            templateName: name,
            content: content,
            subject: subject,
            text_content: text_content,
          });
        })
        .catch(() => {
          // TODO ADD ERR
        });
    } else {
      setTemplateData({
        templateName: 'My new Template',
        subject: `Umfrage von / Survey from / Enquête de / Sondaggio da ${
          params.codename || organization.name
        }`,
        content: '',
        text_content: '  ',
      });
    }
    // eslint-disable-next-line
  }, []);

  const t = useFormatMessage();
  const templateGroup: 'invitation' | 'reminder' =
    path === ROUTES.PROJECT.TEMPLATE.CREATE_EMAIL ||
    path === ROUTES.ADMIN.ORGANIZATIONS.PROJECT.TEMPLATE.CREATE_EMAIL ||
    path === ROUTES.PROJECT.TEMPLATE.EDIT_EMAIL ||
    path === ROUTES.ADMIN.ORGANIZATIONS.PROJECT.TEMPLATE.EDIT_EMAIL
      ? TemplateGroup.INVITATION
      : TemplateGroup.REMINDERS;
  const backToTemplateUrl = params.codename
    ? `/admin/organizations/${params.codename}/projects/${project.id}/collect-data/template`
    : `/project/${project.id}/collect-data/template`;
  const saveTemplateUrl = params.codename
    ? `/admin/organizations/${params.codename}/projects/${project.id}/`
    : `/project/${project.id}/`;

  const saveTemplate = (values: FormValues) => {
    const template = {
      name: values.templateName,
      subject: values.subject,
      text_content: values.text_content,
      group: templateGroup,
      content: generateContent(values.text_content),
    };

    if (templateId) {
      return api.resources.templates
        .updateTemplate({
          orgName: params.codename || organization.codename,
          templateValue: template,
          templateId: Number(templateId),
        })
        .then(() => {
          history.push(
            `${saveTemplateUrl}collect-data/template?updated=success`
          );
        });
    }

    return api.resources.templates
      .saveEmailTemplate({
        orgName: params.codename || organization.codename,
        templateValue: template,
      })
      .then(() => {
        history.push(`${saveTemplateUrl}collect-data/template?created=success`);
      });
  };

  return (
    <Dashboard
      title={project.name || t('page.project', 'Project details')}
      location={location}
    >
      <BackTo onClick={() => history.push(backToTemplateUrl)}>
        {t('templates.base.backToEmail', 'Back to Email Templates')}
      </BackTo>
      <Wrapper>
        <Typography variant="h6" fontSize="1.25rem" weight="medium">
          {t('templates.base.createCustom.title', 'Custom Template')}
        </Typography>
        <Typography
          variant="copy1"
          fontSize="0.875rem"
          weight="regular"
          color="#5C6370"
        >
          {t('templates.base.createCustom', 'Create your invitation template.')}
        </Typography>

        <FormWrapper>
          <Forms.TemplateCreator
            onCancel={() => history.push(backToTemplateUrl)}
            initialValues={templateData}
            onSubmit={saveTemplate}
            isNewTemplate={typeof templateId !== 'string'}
          />
        </FormWrapper>
      </Wrapper>
    </Dashboard>
  );
};

export default compose(
  connect((state) => ({
    organization: selectors.authSelectors.userOrganization(state),
  })),
  withMetaDescription({ title: 'Dashboard projects' }),
  withProject(({ match, organization }: Props) => ({
    projectId: match.params.id,
    organizationCodename: match.params.codename || organization.codename,
  }))
)(TemplateCreator);
